import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ApiServiceBase } from 'src/app/Services/api.service.base';
import { EnforcementService, CreateManualCitationRequest, InfringementTypeResponse, OfficerResponse } from 'src/app/Services/enforcement.service';
import { MediaService } from 'src/app/Services/media.service';
import { Busyable } from 'src/app/shared/editors/busyable';
import { ParkinglotselectorComponent } from 'src/app/features/parking/parking/parkinglots/modals/parkinglotselector/parkinglotselector.component';

interface VehicleColor {
  Id: number;
  Name: string;
}

interface VehicleMake {
  Id: number;
  Name: string;
}

interface ParkingLotSummary {
  Id: string;
  Name: string;
}

@Component({
    selector: 'app-create-citation',
    templateUrl: './create-citation.component.html',
    styleUrls: ['./create-citation.component.scss'],
    standalone: false
})
export class CreateCitationComponent extends Busyable implements OnInit {

  public citationForm!: FormGroup;
  
  // Reference data
  public officers: OfficerResponse[] = [];
  public infringementTypes: InfringementTypeResponse[] = [];
  public vehicleColors: VehicleColor[] = [];
  public vehicleMakes: VehicleMake[] = [];
  public parkingLots: ParkingLotSummary[] = [];
  
  // Selected items
  public selectedParkingLot: ParkingLotSummary | null = null;
  public selectedInfringementTypes: InfringementTypeResponse[] = [];
  public uploadedMediaIds: number[] = [];
  
  // File upload
  public isDragging = false;
  public uploadingFile = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private modalService: SimpleModalService,
    private apiService: ApiServiceBase,
    private enforcementService: EnforcementService,
    private mediaService: MediaService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.initForm();
    await this.loadReferenceData();
  }

  private initForm(): void {
    this.citationForm = this.fb.group({
      Reference: [''],
      IssuedDate: [this.formatDateForInput(new Date())],
      PlateNumber: ['', Validators.required],
      VehicleBrandId: [null],
      VehicleColorId: [null],
      ParkingLotId: [null],
      ParkingSpaceName: [''],
      OfficerId: [null, Validators.required],
      IsWarning: [false],
      Notes: [''],
      EmailAddress: ['', Validators.email]
    });
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().slice(0, 16);
  }

  private async loadReferenceData(): Promise<void> {
    this.Loading();
    try {
      // Load officers
      const officersResult = await this.enforcementService.searchOfficers({
        PageNumber: 1,
        PageSize: 1000,
        Filters: [{ Field: 'IsActive', Operator: 'eq', Value: true }]
      });
      this.officers = officersResult.Results || [];

      // Load infringement types
      this.infringementTypes = await this.enforcementService.getInfringementTypes();

      // Load vehicle makes and colors from the master Vehicles service
      const vehicleData = await this.apiService.Get<any>('/vehicles/makesandcolors');
      this.vehicleColors = (vehicleData?.Colors || []).filter((x: VehicleColor) => x.Name !== '');
      this.vehicleMakes = (vehicleData?.Makes || []).filter((x: VehicleMake) => x.Name !== '');

      // Load parking lots
      const lotsResult = await this.apiService.Get<ParkingLotSummary[]>('/infrastructure/parkinglots/summary');
      this.parkingLots = lotsResult || [];

    } catch (error) {
      console.error('Error loading reference data:', error);
      this.toastr.error('Failed to load reference data', 'Error');
    } finally {
      this.StopLoading();
    }
  }

  public toggleInfringementType(infringementType: InfringementTypeResponse): void {
    const index = this.selectedInfringementTypes.findIndex(it => it.Id === infringementType.Id);
    if (index > -1) {
      this.selectedInfringementTypes.splice(index, 1);
    } else {
      this.selectedInfringementTypes.push(infringementType);
    }
  }

  public isInfringementTypeSelected(infringementType: InfringementTypeResponse): boolean {
    return this.selectedInfringementTypes.some(it => it.Id === infringementType.Id);
  }

  public getTotalFee(): number {
    if (this.citationForm.get('IsWarning')?.value) {
      return 0;
    }
    return this.selectedInfringementTypes.reduce((sum, it) => sum + it.BaseFine, 0);
  }

  public openParkingLotSelector(): void {
    this.modalService.addModal(ParkinglotselectorComponent, {}).subscribe((result: ParkingLotSummary | null) => {
      if (result) {
        this.selectedParkingLot = result;
        this.citationForm.get('ParkingLotId')?.setValue(result.Id);
      }
    });
  }

  public clearParkingLot(): void {
    this.selectedParkingLot = null;
    this.citationForm.get('ParkingLotId')?.setValue(null);
    this.citationForm.get('ParkingSpaceName')?.setValue('');
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFiles(Array.from(input.files));
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.uploadFiles(Array.from(event.dataTransfer.files));
    }
  }

  private uploadFiles(files: File[]): void {
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.uploadingFile = true;
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = (e.target?.result as string).split(',')[1];
          this.mediaService.UploadMedia(base64, file.name, 'citation-evidence', false).subscribe({
            next: (mediaId) => {
              this.uploadedMediaIds.push(mediaId);
              this.uploadingFile = false;
              this.toastr.success(`${file.name} uploaded`, 'Success');
            },
            error: (err) => {
              this.uploadingFile = false;
              this.toastr.error(`Failed to upload ${file.name}`, 'Error');
            }
          });
        };
        reader.readAsDataURL(file);
      } else {
        this.toastr.warning(`${file.name} is not an image file`, 'Warning');
      }
    });
  }

  public removeMedia(index: number): void {
    this.uploadedMediaIds.splice(index, 1);
  }

  public async save(): Promise<void> {
    // Validate form
    if (this.citationForm.invalid) {
      this.citationForm.markAllAsTouched();
      this.toastr.warning('Please fill in all required fields', 'Validation');
      return;
    }

    // Validate infringement types
    if (this.selectedInfringementTypes.length === 0) {
      this.toastr.warning('Please select at least one infringement type', 'Validation');
      return;
    }

    const formValue = this.citationForm.value;

    const request: CreateManualCitationRequest = {
      Reference: formValue.Reference || undefined,
      IssuedDate: formValue.IssuedDate ? new Date(formValue.IssuedDate).toISOString() : undefined,
      PlateNumber: formValue.PlateNumber,
      VehicleBrandId: formValue.VehicleBrandId || null,
      VehicleColorId: formValue.VehicleColorId || null,
      ParkingLotId: formValue.ParkingLotId || null,
      ParkingSpaceName: formValue.ParkingSpaceName || null,
      OfficerId: +formValue.OfficerId,
      InfringementTypeIds: this.selectedInfringementTypes.map(it => it.Id),
      IsWarning: formValue.IsWarning || false,
      Notes: formValue.Notes || null,
      EmailAddress: formValue.EmailAddress || null,
      MediaIds: this.uploadedMediaIds.length > 0 ? this.uploadedMediaIds : null
    };

    this.Busy();
    try {
      const result = await this.enforcementService.createManualCitation(request);
      this.toastr.success(`Citation ${result.Reference} created successfully`, 'Success');
      this.router.navigate(['/enforcement/citations/issued', result.Id]);
    } catch (error: any) {
      console.error('Error creating citation:', error);
      const errorMessage = error?.error?.error || error?.message || 'Failed to create citation';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.StopBusy();
    }
  }

  public cancel(): void {
    this.router.navigate(['/enforcement/citations/issued']);
  }
}

